/**
 * Shopify orders & transactions list APIs (auth required).
 */

import { getApiBaseUrl, getBaseHeaders } from "./api";

function getHeaders({ accessToken } = {}) {
    const headers = { ...getBaseHeaders() };
    if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return headers;
}

function buildQuery(params) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== "") search.set(k, String(v));
    });
    const q = search.toString();
    return q ? `?${q}` : "";
}

/**
 * GET /api/orders
 * @param {{ page?: number, limit?: number, ref_provider?: string, source_ref?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { orders: array, total: number, page: number, limit: number }, error?: string }>}
 */
export async function fetchOrders({ page, limit, ref_provider, source_ref, accessToken } = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ page, limit, ref_provider, source_ref });
    const res = await fetch(`${base}/api/orders${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/transactions
 * @param {{ page?: number, limit?: number, ref_provider?: string, source_ref?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { transactions: array, total: number, page: number, limit: number }, error?: string }>}
 */
export async function fetchTransactions({ page, limit, ref_provider, source_ref, accessToken } = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ page, limit, ref_provider, source_ref });
    const res = await fetch(`${base}/api/transactions${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/overview
 * Financial overview: allTimeEarnings, lastWithdrawal, linkedAccount, monthlyEarnings[]. Auth required.
 * @param {{ accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { allTimeEarnings: number, lastWithdrawal: number, linkedAccount: string, monthlyEarnings: Array<{day: number, earnings: number}>, currency: string }, error?: string }>}
 */
export async function fetchOverview({ accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/overview`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/discount-codes/my
 * @param {{ accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { codes: Array<{ id, profileId, profileName, profileSlug, code, createdAt }> }, error?: string }>}
 */
/**
 * GET /api/shopify/store/products
 * Fetch products directly from user's linked Shopify store.
 * @param {{ first?: number, after?: string, search?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { products: array, pageInfo: { hasNextPage, endCursor } }, error?: string }>}
 */
export async function fetchStoreProducts({ first, after, search, accessToken } = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ first, after, search });
    const res = await fetch(`${base}/api/shopify/store/products${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/shopify/store/products/best-sellers
 * Top products by units sold in paid orders only (empty if none yet).
 * @param {{ first?: number, accessToken?: string }} params
 */
export async function fetchBestSellingProducts({ first = 12, accessToken } = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ first });
    const res = await fetch(`${base}/api/shopify/store/products/best-sellers${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/shopify/store/products/best-sellers/count
 * Returns total number of unique best-selling products (lightweight).
 */
export async function fetchBestSellingProductsCount({ accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/shopify/store/products/best-sellers/count`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, total: json.data.total };
    }
    return { ok: false, total: 0 };
}

/**
 * GET /api/shopify/store/products/:id
 * Fetch single product detail from user's linked Shopify store.
 * @param {{ id: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { product: object }, error?: string }>}
 */
export async function fetchStoreProductDetail({ id, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/shopify/store/products/${encodeURIComponent(id)}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/shopify/store/orders
 * Fetch orders directly from user's linked Shopify store (Admin GraphQL).
 * @param {{ first?: number, after?: string, query?: string, sortKey?: string, reverse?: boolean, accessToken?: string }} params
 */
export async function fetchStoreOrders({ first, after, query, sortKey, reverse, accessToken } = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ first, after, query, sortKey, reverse });
    const res = await fetch(`${base}/api/shopify/store/orders${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/shopify/store/orders/:orderId
 * Fetch single order detail + transactions from Shopify.
 * @param {{ orderId: string, accessToken?: string }} params
 */
export async function fetchStoreOrderDetail({ orderId, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/shopify/store/orders/${encodeURIComponent(orderId)}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/shopify/store/orders/:orderId/transactions
 * Fetch transactions for a specific order from Shopify.
 * @param {{ orderId: string, accessToken?: string }} params
 */
export async function fetchStoreOrderTransactions({ orderId, accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/shopify/store/orders/${encodeURIComponent(orderId)}/transactions`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/shopify/store/connection
 * Check if user has a connected Shopify store.
 * @param {{ accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { connected: boolean, shopDomain: string|null }, error?: string }>}
 */
export async function fetchShopifyConnectionStatus({ accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/shopify/store/connection`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/shopify/store/disconnect
 * Disconnect user's linked Shopify store.
 * @param {{ accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { disconnected: boolean, shopDomain: string }, error?: string }>}
 */
export async function disconnectShopifyStore({ accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/shopify/store/disconnect`, {
        method: "POST",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

export async function fetchMyDiscountCodes({ accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/discount-codes/my`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/shopify/store/collections
 * Fetch store collections with product preview images for Shop by Category widget.
 * @param {{ first?: number, after?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { collections: array, pageInfo: object }, error?: string }>}
 */
/**
 * GET /api/shopify/store/promo-codes
 * Fetch active discount codes from user's linked Shopify store.
 * @param {{ first?: number, search?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { promoCodes: array, pageInfo: object }, error?: string }>}
 */
export async function fetchStorePromoCodes({ first = 20, after, search, accessToken } = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ first, after, search });
    const res = await fetch(`${base}/api/shopify/store/promo-codes${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

export async function fetchStoreCollections({ first = 20, after, accessToken } = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ first, after });
    const res = await fetch(`${base}/api/shopify/store/collections${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/shopify/profile-store-products?profileId=X&ids=gid1,gid2
 * Live current data (title/price/image) for a profile owner's store products, by
 * Shopify Product GID — live-renders brand-ambassador product cards so owner
 * edits always show the latest values. Public (no auth needed on profiles).
 * @param {{ profileId?: number, ids: Array<string>, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { products: array }, error?: string }>}
 */
export async function fetchProfileStoreProductsByIds({ profileId, ids, accessToken } = {}) {
    const list = (ids || []).filter((v) => v != null && v !== "");
    if (list.length === 0) return { ok: true, data: { products: [] } };
    const base = getApiBaseUrl();
    const q = buildQuery({ profileId, ids: list.join(",") });
    const res = await fetch(`${base}/api/shopify/profile-store-products${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

// Pull the numeric Shopify product id out of a GID
// ("gid://shopify/Product/8123456789" → "8123456789"). Returns the input
// unchanged when it's already numeric / a handle so callers can pass either.
function _shopifyNumericId(idOrGid) {
    const s = String(idOrGid || "");
    const m = s.match(/(\d+)\s*$/);
    return m ? m[1] : s;
}

// Normalize the various product-detail payloads (owner admin endpoint and the
// public storefront endpoint return slightly different shapes) into ONE shape
// the product-detail modal can render:
//   { id, title, vendor, descriptionHtml, images:[{id,url,altText}],
//     variants:[{id,title,price,compareAtPrice}], priceRange:{min:{amount,currencyCode}} }
function _normalizeProductDetail(p) {
    if (!p || typeof p !== "object") return null;
    const rawImages = p.images?.edges
        ? p.images.edges.map((e) => e.node)
        : Array.isArray(p.images)
          ? p.images
          : [];
    const images = rawImages
        .map((img, i) => ({
            id: img?.id || String(i),
            url: img?.url || img?.src || img?.originalSrc || "",
            altText: img?.altText ?? null,
        }))
        .filter((img) => img.url);
    if (!images.length && (p.featuredImage?.url || p.imageUrl)) {
        images.push({ id: "featured", url: p.featuredImage?.url || p.imageUrl, altText: null });
    }
    const rawVariants = p.variants?.edges
        ? p.variants.edges.map((e) => e.node)
        : Array.isArray(p.variants)
          ? p.variants
          : [];
    const variants = rawVariants.map((v, i) => ({
        id: v?.id || String(i),
        title: v?.title || v?.selectedOptions?.map((o) => o.value).join(" / ") || "",
        price: String(v?.price?.amount ?? v?.price ?? ""),
        compareAtPrice:
            v?.compareAtPrice?.amount != null
                ? String(v.compareAtPrice.amount)
                : v?.compareAtPrice != null
                  ? String(v.compareAtPrice)
                  : null,
    }));
    const minAmount =
        p.priceRange?.min?.amount ??
        p.priceRangeV2?.minVariantPrice?.amount ??
        p.priceRange?.minVariantPrice?.amount ??
        variants[0]?.price ??
        p.price ??
        null;
    const currencyCode =
        p.priceRange?.min?.currencyCode ??
        p.priceRangeV2?.minVariantPrice?.currencyCode ??
        "USD";
    return {
        id: p.id || "",
        title: p.title || "",
        vendor: p.vendor || "",
        descriptionHtml: p.descriptionHtml || p.description || "",
        images,
        variants,
        priceRange:
            minAmount != null
                ? { min: { amount: String(minAmount), currencyCode } }
                : undefined,
    };
}

/**
 * Fetch full product detail (variants, images, description) for the shared
 * product-detail modal. Resolves the right backend automatically:
 *   • Commission product (solo catalog): commissionProductId → GET /api/commission/products/:id/detail
 *   • Editor (owner authed): accessToken present → GET /api/shopify/store/products/:gid
 *   • Public preview: slug present → GET /api/storefront/:slug/product/:numericId
 * Commission wins over the store endpoints: a commission product lives in the
 * BRAND OWNER's store, not the creator's, so the creator's slug/token can't
 * resolve it.
 * Returns a normalized shape via _normalizeProductDetail.
 * @param {{ productId?: string, commissionProductId?: string|number, slug?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { product: object }, error?: string }>}
 */
export async function fetchModalProductDetail({ productId, commissionProductId, slug, accessToken } = {}) {
    if (!productId && !commissionProductId) return { ok: false, error: "Missing productId" };
    const base = getApiBaseUrl();
    let url;
    if (commissionProductId) {
        url = `${base}/api/commission/products/${encodeURIComponent(commissionProductId)}/detail`;
    } else if (accessToken) {
        url = `${base}/api/shopify/store/products/${encodeURIComponent(productId)}`;
    } else if (slug) {
        url = `${base}/api/storefront/${encodeURIComponent(slug)}/product/${encodeURIComponent(_shopifyNumericId(productId))}`;
    } else {
        return { ok: false, error: "Missing slug or accessToken" };
    }
    try {
        // The commission endpoint is public — sending the creator's token would
        // only invite a CORS preflight for no gain.
        const res = await fetch(url, {
            method: "GET",
            headers: getHeaders(commissionProductId ? {} : { accessToken }),
        });
        const json = await res.json().catch(() => ({}));
        const rawProduct = json?.data?.product || json?.product || json?.data || null;
        const product = _normalizeProductDetail(rawProduct);
        if (res.ok && product) {
            return { ok: true, data: { product } };
        }
        return {
            ok: false,
            error: json?.error?.message || `Request failed (${res.status})`,
        };
    } catch (err) {
        return { ok: false, error: err?.message || "Network error" };
    }
}
